import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "reactstrap";
import { CheckCircle, AlertTriangle, RotateCcw } from "react-feather";
import {
  getKioskMerchantSyncConfig,
  saveKioskMerchantSyncConfig,
  getKioskMerchantSyncOrders,
  resetKioskMerchantSyncOrders,
} from "../../utils/apiCalls";
import { C } from "../reporting/reportingStyles";
import { RCard } from "../reporting/ReportingShared";


const CONFIG_FIELDS = [
  { key: "taxId", label: "Tax ID", hint: "res.company tax_id — put on every line" },
  { key: "defaultCustomerId", label: "Default customer ID", hint: "The walk-in customer (pos_default_customer_id)" },
  { key: "accountId", label: "Payment account ID", hint: "Account `id` of the wags_signage payment method" },
  { key: "paymentMethodId", label: "Payment method ID", hint: "Its `payment_method_id`" },
];

// Per branch, because the merchant's order types are per branch.
const BRANCH_FIELDS = [
  { key: "orderTypeId", label: "Order type ID (Pickup)" },
  { key: "aggregatorId", label: "Aggregator ID (wags_signage)" },
];

const STATUSES = ["pending", "synced", "skipped", "failed", "dead"];

const STATUS_COLORS = {
  synced: [C.greenLight, C.green],
  pending: [C.blueLight, C.blue],
  skipped: [C.orangeLight, C.orange],
  failed: [C.orangeLight, C.orange],
  dead: ["#FDECEC", "#D14343"],
};

const toInput = (value) => (value === null || value === undefined ? "" : String(value));

const KioskMerchantSyncCard = ({ isAdmin }) => {
  const ownMerchantId = localStorage.getItem("MerchantId") || "";
  const { selectedMerchant } = useSelector((state) => state.merchant || {});
  const merchantId = isAdmin ? selectedMerchant || "" : ownMerchantId;

  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [config, setConfig] = useState({});
  const [orderTypes, setOrderTypes] = useState({});
  const [lookbackHours, setLookbackHours] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [statusFilter, setStatusFilter] = useState("skipped");
  const [orders, setOrders] = useState({ rows: [], total: 0 });
  const [selected, setSelected] = useState([]);

  const applyData = useCallback((next) => {
    setData(next);
    if (!next) return;
    setEnabled(!!next.pushKioskOrdersToMerchant);
    setLookbackHours(toInput(next.lookbackHours));
    setConfig(
      CONFIG_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: toInput(next.config?.[f.key]) }), {}),
    );
    setOrderTypes(
      (next.branches || []).reduce(
        (acc, b) => ({ ...acc, [b.id]: BRANCH_FIELDS.reduce((v, f) => ({ ...v, [f.key]: toInput(b[f.key]) }), {}) }),
        {},
      ),
    );
  }, []);

  const branchChanged = (b) => BRANCH_FIELDS.some((f) => (orderTypes[b.id]?.[f.key] || "") !== toInput(b[f.key]));
  const setBranchField = (branchId, key, value) =>
    setOrderTypes((prev) => ({ ...prev, [branchId]: { ...(prev[branchId] || {}), [key]: value.replace(/\D/g, "") } }));

  const loadOrders = useCallback(async (id, status) => {
    if (!id) return;
    const result = await getKioskMerchantSyncOrders(id, { status: status || undefined, limit: 50 });
    setOrders(result || { rows: [], total: 0 });
    setSelected([]);
  }, []);

  const load = useCallback(async (id) => {
    if (!id) {
      setData(null);
      return;
    }
    setIsLoading(true);
    applyData(await getKioskMerchantSyncConfig(id));
    setIsLoading(false);
  }, [applyData]);

  useEffect(() => {
    load(merchantId);
  }, [merchantId, load]);

  useEffect(() => {
    loadOrders(merchantId, statusFilter);
  }, [merchantId, statusFilter, loadOrders]);

  const dirty = useMemo(() => {
    if (!data) return false;
    if (enabled !== !!data.pushKioskOrdersToMerchant) return true;
    if ((lookbackHours || "") !== toInput(data.lookbackHours)) return true;
    if (CONFIG_FIELDS.some((f) => (config[f.key] || "") !== toInput(data.config?.[f.key]))) return true;
    return (data.branches || []).some((b) =>
      BRANCH_FIELDS.some((f) => (orderTypes[b.id]?.[f.key] || "") !== toInput(b[f.key])),
    );
  }, [data, enabled, lookbackHours, config, orderTypes]);

  const missing = useMemo(() => {
    if (!data) return [];
    const list = CONFIG_FIELDS.filter((f) => !data.config?.[f.key]).map((f) => f.label);
    for (const f of BRANCH_FIELDS) {
      const count = (data.branches || []).filter((b) => b.active && !b[f.key]).length;
      if (count) list.push(`${f.label} on ${count} active branch(es)`);
    }
    return list;
  }, [data]);

  const handleSave = async () => {
    if (!merchantId) return;
    setIsSaving(true);
    const result = await saveKioskMerchantSyncConfig(merchantId, {
      pushKioskOrdersToMerchant: enabled,
      lookbackHours: lookbackHours || null,
      config: CONFIG_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: config[f.key] || null }), {}),
      branches: (data?.branches || [])
        .filter(branchChanged)
        .map((b) => ({
          id: b.id,
          ...BRANCH_FIELDS.reduce((v, f) => ({ ...v, [f.key]: orderTypes[b.id]?.[f.key] || null }), {}),
        })),
    });
    setIsSaving(false);
    if (result) applyData(result);
  };

  const handleReset = async (payload) => {
    const result = await resetKioskMerchantSyncOrders(merchantId, payload);
    if (result) {
      load(merchantId);
      loadOrders(merchantId, statusFilter);
    }
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
  const hintStyle = { fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.5 };
  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    fontSize: 13,
    background: C.white,
  };
  const cell = { padding: "8px 10px", fontSize: 12, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" };

  const Chip = ({ status, count }) => {
    const [bg, fg] = STATUS_COLORS[status] || [C.border, C.text];
    return (
      <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: bg, color: fg }}>
        {status}
        {count !== undefined ? ` ${count}` : ""}
      </span>
    );
  };

  const resettable = ["skipped", "failed", "dead"].includes(statusFilter);

  return (
    <RCard>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <h5 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>Kiosk orders → POS backend</h5>
        {data && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: data.pushKioskOrdersToMerchant ? C.greenLight : C.orangeLight,
              color: data.pushKioskOrdersToMerchant ? C.green : C.orange,
            }}
          >
            {data.pushKioskOrdersToMerchant ? <CheckCircle size={13} /> : <AlertTriangle size={13} />}
            {data.pushKioskOrdersToMerchant ? "Sending" : "Off"}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: C.textLight, marginBottom: 16, lineHeight: 1.5 }}>
        Paid screen orders are created on the merchant's POS backend every five minutes, with the
        terminal's card details. Unpaid screen orders are rung up at the counter and are never sent.
      </div>

      {!merchantId ? (
        <div style={{ fontSize: 13, color: C.textLight }}>Pick a merchant in the header to set this up.</div>
      ) : isLoading || !data ? (
        <div style={{ fontSize: 13, color: C.textLight }}>Loading…</div>
      ) : (
        <>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 16 }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Send paid kiosk orders to the POS backend
          </label>

          {missing.length > 0 && (
            <div style={{ fontSize: 12, color: C.orange, marginBottom: 14 }}>
              Missing: {missing.join(", ")}. Orders are skipped until these are filled in.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            {CONFIG_FIELDS.map((f) => (
              <div key={f.key}>
                <label style={labelStyle}>{f.label}</label>
                <input
                  style={inputStyle}
                  inputMode="numeric"
                  value={config[f.key] || ""}
                  onChange={(e) => setConfig((prev) => ({ ...prev, [f.key]: e.target.value.replace(/\D/g, "") }))}
                />
                <div style={hintStyle}>{f.hint}</div>
              </div>
            ))}
            <div>
              <label style={labelStyle}>Look-back (hours)</label>
              <input
                style={inputStyle}
                inputMode="numeric"
                value={lookbackHours}
                onChange={(e) => setLookbackHours(e.target.value.replace(/\D/g, ""))}
              />
              <div style={hintStyle}>
                Paid kiosk orders this far back that were never queued are picked up too (max{" "}
                {data.maxLookbackHours}). Normally 6.
              </div>
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={labelStyle}>Order types per branch</label>
            <div style={{ ...hintStyle, marginTop: 0, marginBottom: 8 }}>
              Order type = the branch's <code>Pickup</code> order type (order and every line). Aggregator = its{" "}
              <code>wags_signage</code> order type (aggregator_id).
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...cell, textAlign: "left" }}>Branch</th>
                    <th style={{ ...cell, textAlign: "left" }}>POS branch ID</th>
                    {BRANCH_FIELDS.map((f) => (
                      <th key={f.key} style={{ ...cell, textAlign: "left", width: 180 }}>{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.branches || []).map((b) => (
                    <tr key={b.id} style={{ opacity: b.active ? 1 : 0.55 }}>
                      <td style={cell}>{b.name}</td>
                      <td style={cell}>{b.merchant_branch_id || "—"}</td>
                      {BRANCH_FIELDS.map((f) => (
                        <td key={f.key} style={cell}>
                          <input
                            style={{ ...inputStyle, padding: "6px 10px" }}
                            inputMode="numeric"
                            value={orderTypes[b.id]?.[f.key] || ""}
                            onChange={(e) => setBranchField(b.id, f.key, e.target.value)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <Button
              onClick={handleSave}
              disabled={!dirty || isSaving}
              style={{
                background: C.coral,
                border: "none",
                borderRadius: 8,
                padding: "9px 20px",
                fontSize: 13,
                fontWeight: 600,
                opacity: !dirty || isSaving ? 0.5 : 1,
              }}
            >
              {isSaving ? "Saving…" : "Save Settings"}
            </Button>
          </div>

          <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 22, paddingTop: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text, marginRight: 4 }}>Orders</span>
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  style={{
                    border: statusFilter === s ? `1px solid ${C.text}` : "1px solid transparent",
                    background: "none",
                    borderRadius: 999,
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <Chip status={s} count={data.counts?.[s] ?? 0} />
                </button>
              ))}
              {resettable && (
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <Button
                    size="sm"
                    outline
                    disabled={!selected.length}
                    onClick={() => handleReset({ orderIds: selected })}
                  >
                    <RotateCcw size={12} /> Re-queue selected
                  </Button>
                  <Button
                    size="sm"
                    outline
                    disabled={!data.counts?.[statusFilter]}
                    onClick={() => handleReset({ status: statusFilter })}
                  >
                    <RotateCcw size={12} /> Re-queue all {statusFilter}
                  </Button>
                </div>
              )}
            </div>

            {orders.rows.length === 0 ? (
              <div style={{ fontSize: 12, color: C.textLight }}>No {statusFilter} orders.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {resettable && <th style={cell} />}
                      <th style={{ ...cell, textAlign: "left" }}>Order</th>
                      <th style={{ ...cell, textAlign: "left" }}>Total</th>
                      <th style={{ ...cell, textAlign: "left" }}>Status</th>
                      <th style={{ ...cell, textAlign: "left" }}>POS order</th>
                      <th style={{ ...cell, textAlign: "left" }}>Reason</th>
                      <th style={{ ...cell, textAlign: "left" }}>Attempts</th>
                      <th style={{ ...cell, textAlign: "left" }}>Last attempt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.rows.map((row) => (
                      <tr key={row.id}>
                        {resettable && (
                          <td style={cell}>
                            <input
                              type="checkbox"
                              checked={selected.includes(row.OrderId)}
                              onChange={(e) =>
                                setSelected((prev) =>
                                  e.target.checked ? [...prev, row.OrderId] : prev.filter((id) => id !== row.OrderId),
                                )
                              }
                            />
                          </td>
                        )}
                        <td style={cell}>
                          <div style={{ fontWeight: 600 }}>#{row.OrderId}</div>
                          <div style={{ color: C.textLight }}>{row.Order?.externalId}</div>
                        </td>
                        <td style={cell}>{row.Order?.grandTotal ?? "—"}</td>
                        <td style={cell}><Chip status={row.status} /></td>
                        <td style={cell}>{row.merchantOrderId || "—"}</td>
                        <td style={{ ...cell, maxWidth: 320, wordBreak: "break-word" }}>{row.reason || "—"}</td>
                        <td style={cell}>{row.attempts}</td>
                        <td style={cell}>{row.lastAttemptAt ? new Date(row.lastAttemptAt).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </RCard>
  );
};

export default KioskMerchantSyncCard;
